import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../../Styles/Development.css";
import { developmentWorksAPI } from "../../api/index";

const PAGE_SIZE = 10;
const CATEGORIES = ["Road Works", "Drainage", "Water Supply"];

// Matches the backend CheckConstraint on development_works.status
const STATUS_OPTIONS = ["pending", "in_progress", "completed"];
const STATUS_LABEL = { pending: "Proposed", in_progress: "Ongoing", completed: "Completed" };

// TEMPORARY client-side guess for mandal_id -> name (API returns only mandal_id).
// CONFIRM the real IDs with the backend before relying on this in production.
const MANDAL_ID_MAP = {
  1: "Kandukur",
  2: "Lingasamudram",
  3: "Gudluru",
  4: "Ulavapadu",
  5: "Voletivaripalem",
};

const num = (v) => (typeof v === "number" ? v : Number(v) || 0);

const extractList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || d?.data || [];
};

const extractTotalCount = (res, fallbackLen) => {
  const d = res?.data;
  return num(d?.total ?? d?.count ?? d?.total_count ?? fallbackLen);
};

const getErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.reason || d.msg).join("; ");
  return fallback;
};

// API row -> shape used by this component
const normalizeRow = (row) => ({
  id: row.id,
  title: row.title || "Untitled",
  desc: row.description || "",
  mandalId: row.mandal_id,
  mandal: MANDAL_ID_MAP[row.mandal_id] || `Mandal #${row.mandal_id}`,
  category: row.category || "",
  status: row.status || "pending",
  estimated_cost: row.estimated_cost ?? "",
  date: row.work_date || "",
});

const AdminDevelopment = () => {
  const navigate = useNavigate();

  const [developments, setDevelopments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusCounts, setStatusCounts] = useState({});

  const [searchInput, setSearchInput] = useState(""); // what the user types
  const [search, setSearch] = useState(""); // debounced value sent to the API
  const [category, setCategory] = useState("All Categories");
  const [status, setStatus] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const requestId = useRef(0); // ignore stale responses

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  /* ---------- class helpers ---------- */
  const getIconClass = (cat) => {
    switch (cat) {
      case "Road Works": return "dev123-icon-road";
      case "Drainage": return "dev123-icon-drainage";
      case "Water Supply": return "dev123-icon-water";
      default: return "dev123-icon-road";
    }
  };

  const getCategoryClass = (cat) => {
    switch (cat) {
      case "Road Works": return "dev123-cat-road-works";
      case "Drainage": return "dev123-cat-drainage";
      case "Water Supply": return "dev123-cat-water-project";
      default: return "";
    }
  };

  const getStatusClass = (st) => {
    switch (st) {
      case "completed": return "dev123-status-completed";
      case "in_progress": return "dev123-status-ongoing";
      case "pending": return "dev123-status-proposed";
      default: return "";
    }
  };

  /* ---------- debounce the search box ---------- */
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* ---------- fetch list (filters + pagination sent as query params) ---------- */
  const fetchDevelopments = useCallback(async () => {
    const myId = ++requestId.current;
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: PAGE_SIZE };
      if (search.trim()) params.search = search.trim();
      if (category !== "All Categories") params.category = category;
      if (status !== "All Status") params.status = status;

      const res = await developmentWorksAPI.getAll(params);
      if (myId !== requestId.current) return; // a newer request has started

      const rows = extractList(res).map(normalizeRow);
      setDevelopments(rows);
      setTotalCount(extractTotalCount(res, rows.length));
    } catch (err) {
      if (myId === requestId.current) toast.error("Could not load development works");
    } finally {
      if (myId === requestId.current) setLoading(false);
    }
  }, [currentPage, search, category, status]);

  // GET /development-works/status-summary -> list[{status, total}]
  const fetchStatusSummary = useCallback(async () => {
    try {
      const res = await developmentWorksAPI.statusSummary();
      const rows = Array.isArray(res.data) ? res.data : [];
      const map = {};
      rows.forEach((r) => { map[r.status] = num(r.total); });
      setStatusCounts(map);
    } catch (err) {
      // non-fatal: cards fall back to totalCount
    }
  }, []);

  useEffect(() => { fetchDevelopments(); }, [fetchDevelopments]);
  useEffect(() => { fetchStatusSummary(); }, [fetchStatusSummary]);

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setCategory("All Categories");
    setStatus("All Status");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  /* ---------- edit ---------- */
  const openEditModal = (item) => {
    setEditItem({ ...item, mandalName: MANDAL_ID_MAP[item.mandalId] || "" });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    if (saving) return;
    setShowEditModal(false);
    setEditItem(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: editItem.title.trim(),
        description: editItem.desc,
        status: editItem.status,
      };
      if (editItem.mandalName) payload.mandal_name = editItem.mandalName;
      if (editItem.category) payload.category = editItem.category;
      if (editItem.estimated_cost !== "" && editItem.estimated_cost != null) {
        payload.estimated_cost = Number(editItem.estimated_cost);
      }
      if (editItem.date) payload.work_date = editItem.date;

      await developmentWorksAPI.update(editItem.id, payload);
      toast.success("Development work updated");
      setShowEditModal(false);
      setEditItem(null);
      fetchDevelopments();
      fetchStatusSummary();
    } catch (err) {
      toast.error(getErrorMessage(err, "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  /* ---------- stat cards ---------- */
  const completed = statusCounts.completed || 0;
  const ongoing = statusCounts.in_progress || 0;
  const proposed = statusCounts.pending || 0;
  const totalProjects = completed + ongoing + proposed || totalCount;
  const pct = (n) => (totalProjects ? ((n / totalProjects) * 100).toFixed(1) : 0);

  // page-number window (max 5 buttons)
  const pageStart = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const pageNumbers = Array.from(
    { length: Math.min(totalPages, 5) },
    (_, i) => pageStart + i
  );

  // keep an unknown/old category selectable in the edit modal
  const editCategories =
    editItem?.category && !CATEGORIES.includes(editItem.category)
      ? [...CATEGORIES, editItem.category]
      : CATEGORIES;

  return (
    <div className="dev123-page">
      {/* Header */}
      <div className="dev123-header">
        <div className="dev123-header-left">
          <div className="dev123-page-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 21h20L12 2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div>
            <h2 className="dev123-title">Development Works</h2>
            <p className="dev123-subtitle">Track and manage all development projects and works</p>
          </div>
        </div>

        <button className="dev123-add-btn" onClick={() => navigate("/AdminDashboard/development-form")}>
          <span className="dev123-add-icon">+</span> Add Development Work
        </button>
      </div>

      {/* Stat Cards */}
      <div className="dev123-stats-grid">
        <div className="dev123-stat-card">
          <div className="dev123-stat-icon dev123-stat-icon-green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="dev123-stat-info">
            <span className="dev123-stat-label">Total Projects</span>
            <span className="dev123-stat-value">{totalProjects}</span>
            <span className="dev123-stat-sub">All time records</span>
          </div>
        </div>

        <div className="dev123-stat-card">
          <div className="dev123-stat-icon dev123-stat-icon-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="dev123-stat-info">
            <span className="dev123-stat-label">Completed</span>
            <span className="dev123-stat-value">{completed}</span>
            <span className="dev123-stat-sub">{pct(completed)}% of total</span>
          </div>
        </div>

        <div className="dev123-stat-card">
          <div className="dev123-stat-icon dev123-stat-icon-orange">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="dev123-stat-info">
            <span className="dev123-stat-label">Ongoing</span>
            <span className="dev123-stat-value">{ongoing}</span>
            <span className="dev123-stat-sub">{pct(ongoing)}% of total</span>
          </div>
        </div>

        <div className="dev123-stat-card">
          <div className="dev123-stat-icon dev123-stat-icon-purple">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M7 3h7l4 4v14a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M14 3v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="dev123-stat-info">
            <span className="dev123-stat-label">Proposed</span>
            <span className="dev123-stat-value">{proposed}</span>
            <span className="dev123-stat-sub">{pct(proposed)}% of total</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="dev123-filters">
        <div className="dev123-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search Development Work..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <select
          className="dev123-select"
          value={category}
          onChange={(e) => { setCategory(e.target.value); setCurrentPage(1); }}
        >
          <option>All Categories</option>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>

        <select
          className="dev123-select"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}
        >
          <option value="All Status">All Status</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
          ))}
        </select>

        <button className="dev123-reset-btn" onClick={resetFilters}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M4 4v6h6M20 20v-6h-6M4.5 15a8 8 0 0014.5 3.5M19.5 9A8 8 0 005 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Reset Filters
        </button>
      </div>

      {/* Table */}
      <div className="dev123-table-wrapper">
        <table className="dev123-table table table-hover">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Title</th>
              <th>Mandal</th>
              <th>Category</th>
              <th>Status</th>
              <th>Date</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="dev123-no-data">Loading…</td></tr>
            ) : developments.length === 0 ? (
              <tr><td colSpan="7" className="dev123-no-data">No development works found.</td></tr>
            ) : (
              developments.map((item, index) => (
                <tr key={item.id}>
                  <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>

                  <td>
                    <div className="dev123-title-cell">
                      <div className={`dev123-title-icon ${getIconClass(item.category)}`}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2L2 21h20L12 2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <div>
                        <div className="dev123-title-text">{item.title}</div>
                        <div className="dev123-title-desc">{item.desc}</div>
                      </div>
                    </div>
                  </td>

                  <td>{item.mandal}</td>

                  <td>
                    <span className={`dev123-category-badge ${getCategoryClass(item.category)}`}>
                      {item.category || "—"}
                    </span>
                  </td>

                  <td>
                    <span className={`dev123-status-badge ${getStatusClass(item.status)}`}>
                      {STATUS_LABEL[item.status] || item.status}
                    </span>
                  </td>

                  <td>
                    <div className="dev123-date-cell">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                      {item.date || "—"}
                    </div>
                  </td>

                  <td>
                    <button className="dev123-edit-btn" onClick={() => openEditModal(item)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="dev123-pagination-bar">
        <span className="dev123-pagination-info">
          Showing {developments.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0}–
          {(currentPage - 1) * PAGE_SIZE + developments.length} of {totalCount} entries
        </span>

        <div className="dev123-pagination-controls">
          <button className="dev123-page-arrow" onClick={() => handlePageChange(currentPage - 1)}>&lt;</button>
          {pageNumbers.map((p) => (
            <button
              key={p}
              className={`dev123-page-num ${currentPage === p ? "dev123-active" : ""}`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}
          <button className="dev123-page-arrow" onClick={() => handlePageChange(currentPage + 1)}>&gt;</button>
        </div>
      </div>

      {/* EDIT MODAL */}
      {showEditModal && editItem && (
        <div className="dev123-modal-overlay" onClick={closeEditModal}>
          <div className="dev123-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dev123-modal-header">
              <h3>Edit Development Work</h3>
              <button className="dev123-modal-close" onClick={closeEditModal}>&times;</button>
            </div>

            <form className="dev123-modal-body" onSubmit={handleEditSave}>
              <div className="dev123-form-group">
                <label>Title</label>
                <input type="text" name="title" value={editItem.title} onChange={handleEditChange} required />
              </div>

              <div className="dev123-form-group">
                <label>Description</label>
                <textarea name="desc" value={editItem.desc} onChange={handleEditChange} rows="3" />
              </div>

              <div className="dev123-form-row">
                <div className="dev123-form-group">
                  <label>Mandal</label>
                  <select name="mandalName" value={editItem.mandalName} onChange={handleEditChange}>
                    <option value="">Select Mandal</option>
                    {Object.values(MANDAL_ID_MAP).map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div className="dev123-form-group">
                  <label>Category</label>
                  <select name="category" value={editItem.category} onChange={handleEditChange}>
                    <option value="">Select Category</option>
                    {editCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="dev123-form-row">
                <div className="dev123-form-group">
                  <label>Status</label>
                  <select name="status" value={editItem.status} onChange={handleEditChange}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>

                <div className="dev123-form-group">
                  <label>Date</label>
                  <input type="date" name="date" value={editItem.date} onChange={handleEditChange} />
                </div>
              </div>

              <div className="dev123-form-group">
                <label>Estimated Cost</label>
                <input
                  type="number"
                  min="0"
                  name="estimated_cost"
                  value={editItem.estimated_cost}
                  onChange={handleEditChange}
                />
              </div>

              <div className="dev123-modal-footer">
                <button type="button" className="dev123-btn-cancel" onClick={closeEditModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="dev123-btn-save" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDevelopment;